/**
 * Typed wrappers for assistant-ui's opaque `unstable_data` entries.
 * Replaces `(unstableData as any[])?.find((d: any) => d?.type === "...")`.
 */
import type { DocumentCitationData, WebSourceData } from "@/components/custom/chatModelAdapters";
import type { TokenUsageData } from "@/lib/tokenFormat";

// --- unstable_data entry types ---

export type TerminalStateEntry = {
  type: "terminal-state";
  state: string;
};

export type CitationsEntry = {
  type: "citations";
  citations: DocumentCitationData[];
};

export type WebSourcesEntry = {
  type: "web-sources";
  sources: WebSourceData[];
};

export type ModelEntry = {
  type: "model";
  modelId: string;
};

export type UsageEntry = {
  type: "usage";
} & TokenUsageData;

export type RetryInfoEntry = {
  type: "retry-info";
  code: string;
  message: string | null;
  retryAfterSec: number;
};

export type BackendMessageIdEntry = {
  type: "backend-message-id";
  id: string;
};

/**
 * Ids of generated documents this assistant message produced. Persisted on the
 * `AssistantThreadMessage.GeneratedDocumentIds` column server-side; the frontend
 * surfaces them as inline footer cards beneath the message.
 */
export type GeneratedDocumentsEntry = {
  type: "generated-documents";
  ids: string[];
};

/**
 * MCP `elicitation/create` round-trip — praetor-mcp v5.7.0 scope-filter protocol. Server
 * publishes `data-mcp-elicitation` when an out-of-scope tool call needs lawyer approval;
 * the chat adapter parks the entry on the assistant message's `unstable_data` and
 * `<MessageMcpElicitation />` renders an inline approval card. `id` is the elicit-id
 * minted server-side and reused as the path component of
 * `POST /api/chat/elicitation/{id}/respond`. `schema` carries the MCP RequestedSchema
 * (typically a single enum-of-strings `decision` property). `context` is the server's
 * structured `_meta.praetorScopeContext` (kind / caseId / documentId / toolName) used
 * for display + the client-side accumulated-approvals mirror.
 */
export type McpElicitationEntry = {
  type: "mcp-elicitation";
  id: string;
  message: string;
  // SDK serialises RequestedSchema as a JSON object — exact shape varies per request
  // so we keep it as `unknown` on the wire and narrow inside ElicitationCard.
  schema: unknown;
  context: unknown;
};

export type PraetorRevisionsEntry = {
  type: "praetor-revisions";
  revisions: { id: string; title?: string }[];
};

export type TimingEntry = {
  type: "timing";
  startedAtUtc: string | null;
  totalDurationMs: number | null;
};

export type UnstableDataEntry =
  | TerminalStateEntry
  | CitationsEntry
  | WebSourcesEntry
  | ModelEntry
  | UsageEntry
  | RetryInfoEntry
  | BackendMessageIdEntry
  | GeneratedDocumentsEntry
  | McpElicitationEntry
  | PraetorRevisionsEntry
  | TimingEntry;

// --- Typed lookup helper ---

type EntryTypeMap = {
  "terminal-state": TerminalStateEntry;
  citations: CitationsEntry;
  "web-sources": WebSourcesEntry;
  model: ModelEntry;
  usage: UsageEntry;
  "retry-info": RetryInfoEntry;
  "backend-message-id": BackendMessageIdEntry;
  "generated-documents": GeneratedDocumentsEntry;
  "mcp-elicitation": McpElicitationEntry;
  "praetor-revisions": PraetorRevisionsEntry;
  timing: TimingEntry;
};

/**
 * Type-safe lookup in `message.metadata.unstable_data`.
 * Returns the first entry matching `type`, or `undefined`.
 */
export function findUnstableEntry<K extends keyof EntryTypeMap>(
  data: unknown,
  type: K,
): EntryTypeMap[K] | undefined {
  if (!Array.isArray(data)) return undefined;
  return data.find(
    (d: unknown): d is EntryTypeMap[K] =>
      d != null && typeof d === "object" && (d as { type?: string }).type === type,
  );
}
